import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Overview = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Guild is a platform for communities to host{' '}
            <Link href='/docs/events/overview'>Events</Link>, share{' '}
            <Link href='/docs/presentations/overview'>Presentations</Link>, and manage{' '}
            <Link href='/docs/guilds/members'>Members</Link> — all in one place.
            Whether you run a local meetup group, a developer community, or a
            professional network, Guild gives you the tools to bring people together.
          </Trans>
        </Paragraph>
      </Card>

      <Card gap='$4'>
        <DocSection id='core-concepts' title={t`Core Concepts`}>
          <Paragraph size='$5'>
            <Trans>Everything on Guild is built around four main entities:</Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='people' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Guilds</Paragraph> are communities.
                  A Guild is your group's home page — it has a profile, a list of upcoming
                  Events, a library of Presentations, and a roster of Members. Think of it
                  as your community's hub.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='calendar' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Events</Paragraph> are gatherings.
                  They can be in-person, online, or hybrid. Events support RSVPs,
                  ticketing with multiple tiers, attendee check-in, and a schedule of
                  Presentations.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='projection-screen' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Presentations</Paragraph> are talks
                  and content shared within a community. They can be part of an Event
                  schedule or stand on their own as resources for your Guild.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='person' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Members</Paragraph> are the people
                  who make up a Guild. Members can have different roles — Organizers manage
                  the Guild, Editors help create content, and Members participate in Events
                  and discussions.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='get-started' title={t`Getting Started`}>
          <Paragraph size='$5'>
            <Trans>
              To get started, sign up for an account and join an existing Guild, or
              create your own. You can browse public Guilds and Events without an
              account, but you will need to sign in to RSVP, join a Guild, or create
              content.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              The fastest way to take action on Guild is through the{' '}
              <Link href='/docs/getting-started/command-menu'>Command Palette</Link> — a
              search and action interface built into the top navigation bar. From
              there you can create Guilds, Events, and Presentations, or use{' '}
              <Link href='/docs/getting-started/search'>Search</Link> to find
              communities and events that match your interests.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              To learn how to move around the platform, see{' '}
              <Link href='/docs/getting-started/navigation'>Navigating Guild</Link>.
            </Trans>
          </Paragraph>
          <DocScreenshot
            src='docs/getting-started/command-menu-open.png'
            alt='Command Palette open showing search input and quick actions: Create an Event, Create a Presentation, Create a Guild'
            caption={t`The Command Palette — the fastest way to create and find things on Guild`}
          />
        </DocSection>
      </Card>
    </>
  )
}

export default Overview
